import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import BreakingNews from '@/assets/icons/cover-images/bpdailybreakingnews.png';
import BpDailyLogo from '@/assets/icons/cover-images/bpdailylogo.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadSelectedImagesAsZip,
  downloadImagesAsZip as downloadZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var12');

            // Wait for font to load before measuring text
            await document.fonts.ready;

            const fullImage = new Image();
            fullImage.src = variation.imageSrc;
            fullImage.classList.add('full-image-var12');

            const imageWrap = document.createElement('div');
            imageWrap.classList.add('image-wrap-var12');
            imageWrap.appendChild(fullImage);
            imageHolder.appendChild(imageWrap);

            const watermark = createWatermarkTagImage(
              'actual-photo-var12',
              variation.watermarkTag,
              variation.watermarkPosition,
            );
            if (watermark) {
              if (variation.breakingNews) {
                watermark.classList.add('actual-photo-var12--breaking-news');
              }
              imageWrap.appendChild(watermark);
            }

            const watermarkImg = new Image();
            watermarkImg.classList.add('watermark-var12');
            watermarkImg.src = BpDailyLogo;
            imageHolder.appendChild(watermarkImg);

            const bottomText = document.createElement('div');

            if (variation.circleImageSrc) {
              const circleImage = new Image();
              circleImage.src = variation.circleImageSrc;
              circleImage.classList.add('circle-image-var12');
              imageHolder.appendChild(circleImage);
            }

            if (variation.text) {
              const bottomTextP = document.createElement('p');
              bottomText.classList.add('bottom-text-var12');
              let text = variation.text;

              const algorithmResult = getOptimalFontSize(text, {
                maxWidth: 660,
                maxHeight: 260,
                fontFamily: 'MerriweatherBlack',
                minFontSize: 45,
                maxFontSize: 120,
                lineHeight: '1.35',
              });

              if (variation.textBrakes) {
                bottomTextP.innerText = text;
              } else {
                bottomTextP.innerHTML = text;
                bottomTextP.innerHTML = algorithmResult.lines.join('<br/>');
              }

              if (variation.textFontSize) {
                bottomTextP.style.fontSize = `${variation.textFontSize}px`;
              } else {
                bottomTextP.style.fontSize = `${algorithmResult.fontSize}px`;
              }

              bottomText.appendChild(bottomTextP);
            }

            imageHolder.appendChild(bottomText);

            if (variation.breakingNews) {
              const breakingNewsImg = new Image();
              breakingNewsImg.classList.add('breaking-news-var-11');
              breakingNewsImg.src = BreakingNews;
              imageHolder.appendChild(breakingNewsImg);
              if (breakingNewsImg.decode) {
                try {
                  await breakingNewsImg.decode();
                } catch {}
              } else {
                await new Promise<void>((res) => {
                  if (breakingNewsImg.complete) return res();
                  breakingNewsImg.onload = () => res();
                  breakingNewsImg.onerror = () => res();
                });
              }
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var12',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
